import React, { useState } from "react";

interface LaunchHeroStepProps {
  onComplete: () => void;
  onNext: () => void;
  canProceed: boolean;
}

export const LaunchHeroStep: React.FC<LaunchHeroStepProps> = ({
  onComplete,
  onNext,
  canProceed,
}) => {
  const [name, setName] = useState("");
  const [showWelcome, setShowWelcome] = useState(false);

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      setShowWelcome(true);
      onComplete();

      // Store user name
      localStorage.setItem("herocon_user_name", name.trim());

      // Dispatch custom event to notify AuthHeader of the name change
      window.dispatchEvent(
        new CustomEvent("localStorageChange", {
          detail: { key: "herocon_user_name", value: name.trim() },
        })
      );
    }
  };

  const handleGetStarted = () => {
    onNext();
  };

  return (
    <div className="max-w-4xl mx-auto">
      {!showWelcome ? (
        // Name input form - Step 1
        <div className="max-w-2xl mx-auto">
          <div className="mb-6 mt-6">
            <h3 className="text-2xl text-gray-800 mb-4 text-left">
              What if you could confidently say, "
              <strong>Yes, this project is ready</strong>"?
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed text-left mb-6">
              We help{" "}
              <strong>
                designers, plan reviewers, project managers, and general
                contractors
              </strong>{" "}
              do exactly that. Our technology isn't just about navigating
              regulations; it's about providing peace of mind and unlocking
              professional growth for you and your team.
            </p>
            <h2 className="text-3xl font-bold text-gray-800 mb-10 text-left mt-8">
              Welcome to <strong className="text-green-600">YOUR</strong> code
              compliance hub!
            </h2>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 text-center">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">
              Let's get started
            </h3>
            <form onSubmit={handleNameSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-lg font-medium text-gray-700 mb-3"
                >
                  What's your name?
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-lg focus:ring-4 focus:ring-blue-500 focus:border-blue-500 text-lg transition-all duration-200 hover:border-gray-400"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={!name.trim()}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
              >
                Continue to HeroCon →
              </button>
            </form>
          </div>
        </div>
      ) : (
        // Welcome message - Step 2
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Hi{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                {name}
              </span>{" "}
              my name is Hero!
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              <strong className="text-green-600">My mission</strong> is to
              empower you as the{" "}
              <strong className="text-green-600">best employee</strong> and keep
              you on track for a{" "}
              <strong className="text-green-600">fast permit.</strong>
            </p>
            <p className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mt-4">
              Design with confidence. Build without penalties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 text-center hover:shadow-xl hover:scale-110 transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                AI-Powered Analysis
              </h3>
              <p className="text-gray-600 text-sm">
                Real-time compliance feedback Define % from plan as review
                starting point
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 text-center hover:shadow-xl hover:scale-110 transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Code Compliance
              </h3>
              <p className="text-gray-600 text-sm">
                Constantly updated codes by jurisdiction And used by state
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 text-center hover:shadow-xl hover:scale-110 transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Smart Extraction
              </h3>
              <p className="text-gray-600 text-sm">
                Prevent rework and accelerate permitting
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200 text-center mb-6 hover:scale-110 transition-all duration-300">
            <h3 className="text-xl font-bold text-gray-800 mb-3">
              Ready to optimize your plan review process?
            </h3>
            <p className="text-base text-gray-700">
              Join thousands of professionals who have already transformed their
              plan review process with HeroCon!
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-4 px-8 rounded-lg font-bold text-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              Launch Hero
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
